/**
 * Krok 3: Oferta — pakiety usług z cennikiem.
 * Każda usługa: nazwa, opis, cena, typ ceny.
 */
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Briefcase, CheckCircle2, GripVertical } from 'lucide-react';

const PRICE_TYPES = [
  { value: 'fixed', label: 'Stała cena' },
  { value: 'from', label: 'Od' },
  { value: 'hourly', label: 'Za godzinę' },
  { value: 'free', label: 'Bezpłatne' },
  { value: 'contact', label: 'Wycena indywidualna' },
];

function makeService() {
  return { id: Math.random().toString(36).slice(2, 10), name: '', description: '', price: '', price_type: 'fixed' };
}

export default function Step3Services({ data, updateData }) {
  const services = data.services || [];

  const update = (idx, patch) => {
    const next = services.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    updateData({ services: next });
  };

  const add = () => {
    updateData({ services: [...services, makeService()] });
  };

  const remove = (idx) => {
    updateData({ services: services.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-8" data-testid="business-wizard-v2-step-services">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-orange-600">Krok 3 z 7</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Co oferujesz klientom?</h2>
        <p className="text-sm text-gray-500 mt-1">Dodaj pakiety usług z cenami. To pierwsza rzecz, jakiej szuka klient.</p>
      </header>

      {services.length === 0 ? (
        <div className="border-2 border-dashed border-orange-200 rounded-2xl p-8 text-center bg-orange-50/30">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-100 flex items-center justify-center mb-3">
            <Briefcase className="w-7 h-7 text-orange-500" />
          </div>
          <p className="font-semibold text-gray-900">Dodaj pierwszą usługę</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">Możesz dodać dowolną liczbę — np. „Strzyżenie damskie”, „Koloryzacja”.</p>
          <Button type="button" onClick={add} data-testid="bwv2-add-service-btn" className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj usługę
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {services.map((s, idx) => (
            <div key={s.id || idx} className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 relative" data-testid={`bwv2-service-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                  <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs flex items-center justify-center">{idx + 1}</span>
                  <GripVertical className="w-4 h-4 text-gray-300" />
                  Usługa #{idx + 1}
                </div>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`bwv2-remove-service-${idx}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Nazwa usługi *</Label>
                  <Input
                    value={s.name || ''}
                    onChange={(e) => update(idx, { name: e.target.value })}
                    placeholder="np. Strzyżenie damskie"
                    data-testid={`bwv2-service-name-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Krótki opis</Label>
                  <Textarea
                    value={s.description || ''}
                    onChange={(e) => update(idx, { description: e.target.value })}
                    placeholder="Co zawiera ta usługa, dla kogo, ile trwa..."
                    rows={2}
                    data-testid={`bwv2-service-desc-${idx}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Typ ceny</Label>
                    <select
                      value={s.price_type || 'fixed'}
                      onChange={(e) => update(idx, { price_type: e.target.value })}
                      data-testid={`bwv2-service-pricetype-${idx}`}
                      className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm bg-white"
                    >
                      {PRICE_TYPES.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 mb-1 block">Cena (zł)</Label>
                    <Input
                      type="number"
                      value={s.price || ''}
                      onChange={(e) => update(idx, { price: e.target.value })}
                      placeholder={['free', 'contact'].includes(s.price_type) ? 'nie dotyczy' : '120'}
                      disabled={['free', 'contact'].includes(s.price_type)}
                      data-testid={`bwv2-service-price-${idx}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            onClick={add}
            data-testid="bwv2-add-service-btn"
            variant="outline"
            className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj kolejną usługę
          </Button>
        </div>
      )}
    </div>
  );
}
